// Page Notes gate (spec docs/_mockups/page-notes-send-bell/SPEC.md §2): the tenant's writer roles
// (capabilities.pageNotesRoles, owner/admin always), flag on, desktop >= 1024 px. The toolbar file (the only
// importer of 'agentation') is lazy-loaded, so other roles never download the package.
import { lazy, Suspense } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageNotesMode } from './pageNotesMode';

const LazyToolbar = lazy(() => import('./PageNotesToolbar'));
const ALWAYS = ['owner', 'admin'];
const norm = (r: unknown) => String(r ?? '').trim().toLowerCase();
export const canWriteNotes = (role: string | null | undefined, roles: readonly string[] | null | undefined): boolean => {
  const r = norm(role);
  return !!r && (ALWAYS.includes(r) || (roles ?? []).map(norm).includes(r));
};

/** True when this user is a page-notes writer (flag on, writer role) on any screen size. The App Tour (M22) uses it. */
export function usePageNotesWriter(): boolean {
  const role = useAuthStore(s => s.user?.role?.name);
  const roles = useAuthStore(s => s.tenant?.capabilities?.pageNotesRoles);
  const on = useAuthStore(s => s.tenant?.capabilities?.pageNotes === true);
  return on && canWriteNotes(role, roles);
}

/** True when this user may write page notes here (writer, desktop >= 1024 px). The Kiaan button uses it. */
export function usePageNotesAllowed(): boolean {
  const writer = usePageNotesWriter();
  const desktop = useMediaQuery('(min-width: 1024px)');
  return writer && desktop;
}

// 2026-10-16: the toolbar now loads only after the Kiaan "Page notes" button switches notes mode on.
export function PageNotesGate() {
  const allowed = usePageNotesAllowed();
  const active = usePageNotesMode();
  if (!allowed || !active) return null;
  return <Suspense fallback={null}><LazyToolbar /></Suspense>;
}
